import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/ui/button";
import {
  ListView,
  ListFetchParams,
  ListFetchResult,
} from "../../../Core/Components/ListView/ListView";
import { Input } from "@/ui/input";
import { cn } from "@/lib/utils";
import { QrCode, MoreVertical } from "lucide-react";
import QRCodeModal from "../../../Core/Components/QRCodeSlider/QRCodeModal";
import QRCodeSheet from "../Components/ContentSlider/QRCodeSheet";
import RFProfileSetupSheet from "../Components/ContentSlider/RFProfileSetupSheet";
import { RFPROFILESETUP } from "../../../../constants/applicationConstants";
import {
  listRFProfileSetup,
  deleteRFProfileSetup,
} from "../Services/RFProfileSetupService";
import { getTokensFromStorage } from "../../../../Utils/Auth/AuthToken";
import {
  objectToBase64,
  base64ToObject,
} from "../../../../Utils/Common/commonUtils";
import { TOGGLE_PROFILE_SETUP_SLIDER } from "../../../../constants/actionTypes";

type RFProfileRef = {
  SRFProfileId: string | number;
  [k: string]: any;
};

type RFProfileSetupItem = {
  SRFProfilesSetupId: string | number;
  SRFProfilesSetupName: string;
  SRFProfilesDetails?: string;
  WHID?: string | number;
  RFVenName?: string;
  MTFURL?: string;
  MTFPort?: string | number;
  MTFProtocol?: string;
  auto_devcod_seqnum?: string | number;
  created_by?: string;
  created_date?: string;
  modified_by?: string;
  modified_date?: string;
  [k: string]: any;
};

type Props = {
  profile: RFProfileRef;
};

function hasValue(v: any): boolean {
  return v !== null && v !== undefined && String(v).trim() !== "";
}

function formatDateShort(d: string | Date | undefined): string {
  try {
    if (!d) return "";
    const date = typeof d === "string" ? new Date(d) : d;
    if (Number.isNaN(date.getTime())) return String(d);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  } catch {
    return String(d as any);
  }
}

let setupSearchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let lastSetupSearchTerm = "";
const RFProfileSetupTSX: React.FC<Props> = ({ profile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showQRModal, setShowQRModal] = React.useState(false);
  const [singleProfile, setSingleProfile] =
    React.useState<RFProfileSetupItem | null>(null);
  const [refreshTick, setRefreshTick] = React.useState(0);
  const [searchTerm, setSearchTerm] = React.useState("");

  // Slider (Add/Edit) state
  const [sliderShow, setSliderShow] = React.useState(false);
  const [sliderActions, setSliderActions] = React.useState<any>({
    action: TOGGLE_PROFILE_SETUP_SLIDER,
    actionMeta: null,
    bulkAction: TOGGLE_PROFILE_SETUP_SLIDER,
  });
  const handleSlide = React.useCallback(() => {
    // Close slider when child requests
    setSliderShow(false);
  }, []);

  const [setupConfirmOpen, setSetupConfirmOpen] = React.useState(false);
  const setupDeleteConfirmRef = React.useRef<(() => void) | null>(null);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const qr = params.get("qrData");
    if (qr) {
      try {
        const decoded = base64ToObject(qr as any);
        if (decoded) {
          setSingleProfile(decoded as any);
          setShowQRModal(true);
        }
      } catch {}
    }
  }, [location.search]);

  React.useEffect(() => {
    if (!showQRModal) {
      const params = new URLSearchParams(location.search || "");
      if (params.has("qrData")) {
        params.delete("qrData");
        navigate({ search: params.toString() }, { replace: true });
      }
    }
  }, [showQRModal, location.search, navigate]);

  // Deep-link: open Edit slider via ?profileSetups=<base64({ SRFProfilesSetupId })>
  React.useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const ps = params.get("profileSetups");
    if (!ps) return;
    // Require profile context before opening (sheet depends on it)
    if (!profile || !(profile as any)?.SRFProfileId) return;
    try {
      const decoded = base64ToObject(ps as any);
      if ((decoded as any)?.SRFProfilesSetupId) {
        setSliderActions({
          action: TOGGLE_PROFILE_SETUP_SLIDER,
          actionMeta: {
            title: "edit",
            profile,
            checkedDataItem: decoded,
            edit: true,
          },
          bulkAction: TOGGLE_PROFILE_SETUP_SLIDER,
        });
        // Defer opening until actions state is set
        setTimeout(() => setSliderShow(true), 0);
      }
    } catch {}
  }, [location.search, profile]);

  // When slider closes, remove profileSetups from URL (guard initial mount)
  const prevSliderShow = React.useRef(false);
  React.useEffect(() => {
    if (prevSliderShow.current && !sliderShow) {
      const params = new URLSearchParams(location.search || "");
      if (params.has("profileSetups")) {
        params.delete("profileSetups");
        navigate({ search: params.toString() }, { replace: true });
      }
    }
    prevSliderShow.current = sliderShow;
  }, [sliderShow, location.search, navigate]);

  const handleGenerateQRCode = (item: RFProfileSetupItem) => {
    setSingleProfile(item);
    const params = new URLSearchParams(location.search || "");
    params.set("qrData", objectToBase64(item as any));
    navigate({ search: params.toString() }, { replace: true });
    setShowQRModal(true);
  };

  const fetchList = React.useCallback(
    async (
      params: ListFetchParams,
    ): Promise<ListFetchResult<RFProfileSetupItem>> => {
      const { page, pageSize, search, sort, filters } = params;

      // Build server filters with OR chain for search
      const term = ((filters as any)?.searchTerm ?? search ?? "").trim();
      let serverFilters: any[] = Array.isArray(filters)
        ? [...(filters as any)]
        : [];
      if (term) {
        const base = [
          {
            ColumnName: "SRFProfilesSetupName",
            Op: "contains",
            ColumnValue: term,
          },
          {
            ColumnName: "SRFProfilesDetails",
            Op: "contains",
            ColumnValue: term,
          },
          { ColumnName: "RFVenName", Op: "contains", ColumnValue: term },
          { ColumnName: "MTFURL", Op: "contains", ColumnValue: term },
          { ColumnName: "created_by", Op: "contains", ColumnValue: term },
          { ColumnName: "modified_by", Op: "contains", ColumnValue: term },
        ];
        const hasExisting = serverFilters.length > 0;
        const searchFilters = base.map((f, idx) =>
          idx === 0 && !hasExisting ? f : { LogicalOperator: "OR", ...f },
        );
        serverFilters = [...serverFilters, ...searchFilters];
      }

      const doRequest = async (): Promise<
        ListFetchResult<RFProfileSetupItem>
      > => {
        const { accessToken } = (getTokensFromStorage() as any) || {};
        const resp = await listRFProfileSetup({
          accessToken,
          limit: pageSize,
          offset: page, // service treats offset as pageNumber (1-based)
          filter: serverFilters,
          sort: sort
            ? [{ field: (sort as any).field, dir: (sort as any).dir }]
            : undefined,
          moduleName: RFPROFILESETUP,
          isServiceWorker: false,
          listController: undefined,
          profile,
        });

        const items = (resp as any)?.ProfileSetups ?? [];
        const total = (resp as any)?.TotalCount ?? 0;

        return { items, total };
      };

      // Debounce only when the search term changes (typing). For pagination/sort (term unchanged), request immediately.
      if (term !== lastSetupSearchTerm) {
        lastSetupSearchTerm = term;
        if (setupSearchDebounceTimer) clearTimeout(setupSearchDebounceTimer);
        return new Promise<ListFetchResult<RFProfileSetupItem>>((resolve) => {
          setupSearchDebounceTimer = setTimeout(async () => {
            const result = await doRequest();
            resolve(result);
          }, 350);
        });
      }

      return await doRequest();
    },
    [profile],
  );

  const onDelete = React.useCallback(async (selected: RFProfileSetupItem[]) => {
    for (const it of selected) {
      try {
        await deleteRFProfileSetup({
          data: it,
          moduleName: RFPROFILESETUP,
          isServiceWorker: false,
          listController: undefined,
        });
      } catch {
        // swallow per-item errors; ListView will refresh after handler completes
      }
    }
    // bump refresh if needed (ListView already refreshes after onDelete)
    setRefreshTick((t) => t + 1);
  }, []);

  const openEdit = React.useCallback(
    (item: RFProfileSetupItem) => {
      // Mirror QR pattern: update URL with profileSetups so refresh/share works
      try {
        const params = new URLSearchParams(location.search || "");
        params.set(
          "profileSetups",
          objectToBase64({
            SRFProfilesSetupId: (item as any)?.SRFProfilesSetupId,
          }),
        );
        navigate({ search: params.toString() }, { replace: true });
      } catch {}

      setSliderActions({
        action: TOGGLE_PROFILE_SETUP_SLIDER,
        actionMeta: {
          title: "edit",
          profile,
          checkedDataItem: item,
          edit: true,
        },
        bulkAction: TOGGLE_PROFILE_SETUP_SLIDER,
      });
      setSliderShow(true);
    },
    [profile, location.search, navigate],
  );

  const handleDeleteOne = React.useCallback((item: RFProfileSetupItem) => {
    setupDeleteConfirmRef.current = async () => {
      await deleteRFProfileSetup({
        data: item,
        moduleName: RFPROFILESETUP,
        isServiceWorker: false,
        listController: undefined,
      });
      setRefreshTick((t) => t + 1);
    };
    setSetupConfirmOpen(true);
  }, []);

  const RowActions: React.FC<{ onDeleteClick: () => void }> = ({
    onDeleteClick,
  }) => {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
      if (!open) return;
      const onDoc = (e: MouseEvent) => {
        const t = e.target as Node;
        if (ref.current && !ref.current.contains(t)) setOpen(false);
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") setOpen(false);
      };
      document.addEventListener("mousedown", onDoc);
      document.addEventListener("keydown", onKey);
      return () => {
        document.removeEventListener("mousedown", onDoc);
        document.removeEventListener("keydown", onKey);
      };
    }, [open]);

    return (
      <div ref={ref} className="relative">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          aria-label="More actions"
          title="More actions"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
        {open && (
          <div
            role="menu"
            className="absolute right-0 mt-2 w-40 rounded-md border border-border bg-popover text-popover-foreground shadow-md z-50"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              role="menuitem"
              className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-accent hover:text-accent-foreground cursor-pointer"
              onClick={() => {
                onDeleteClick();
                setOpen(false);
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    );
  };

  const itemRenderer = (item: RFProfileSetupItem) => {
    const showUpdated = hasValue(item.modified_date);
    const dateToShow = showUpdated ? item.modified_date : item.created_date;
    const byToShow = showUpdated ? item.modified_by : item.created_by;

    return (
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] items-start sm:items-stretch gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex w-full items-center gap-2">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <div
                className="truncate text-lg font-semibold text-foreground cursor-pointer hover:underline"
                onClick={() => openEdit(item)}
                title="View / Edit"
              >
                {item.SRFProfilesSetupName}
              </div>
              {(item as any)?.tenant_name && (
                <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300 px-2 py-0.5 text-xs font-medium">
                  {(item as any)?.tenant_name}
                </span>
              )}
            </div>
          </div>

          {!!item.SRFProfilesDetails && (
            <div className="mt-2 text-base text-muted-foreground line-clamp-2">
              {item.SRFProfilesDetails}
            </div>
          )}

          <div className="mt-2 flex items-center gap-6 flex-wrap md:flex-nowrap">
            <div className="min-w-0">
              <div className="flex items-baseline gap-2 text-sm">
                <span className="font-semibold whitespace-nowrap shrink-0">
                  Warehouse ID:
                </span>
                <span className="text-foreground truncate">
                  {String(item.WHID ?? "") || "-"}
                </span>
              </div>
            </div>
            <div className="min-w-0">
              <div className="flex items-baseline gap-2 text-sm">
                <span className="font-semibold whitespace-nowrap shrink-0">
                  MTF URL:
                </span>
                <span
                  className="text-foreground truncate"
                  title={(item as any)?.MTFURL || ""}
                >
                  {(item as any)?.MTFURL || "-"}
                </span>
              </div>
            </div>
            <div className="min-w-0">
              <div className="flex items-baseline gap-2 text-sm">
                <span className="font-semibold whitespace-nowrap shrink-0">
                  MTF Port:
                </span>
                <span className="text-foreground truncate">
                  {String((item as any)?.MTFPort ?? "") || "-"}
                </span>
              </div>
            </div>
            <div className="min-w-0">
              <div className="flex items-baseline gap-2 text-sm">
                <span className="font-semibold whitespace-nowrap shrink-0">
                  MTF Protocol:
                </span>
                <span className="text-foreground truncate">
                  {(item as any)?.MTFProtocol || "-"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right-side actions/meta: collapses better on mobile */}
        <div className="flex flex-row sm:flex-col justify-between items-center sm:items-end text-left sm:text-right gap-2 w-full sm:w-auto">
          <div className="order-2 sm:order-1 ml-auto sm:ml-0 shrink-0 flex items-center gap-1">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-10 w-10"
              onClick={(e) => {
                e.stopPropagation();
                handleGenerateQRCode(item);
              }}
              title="Show QR"
              aria-label="Show QR"
            >
              <QrCode className="h-10 w-10" />
            </Button>
            <RowActions onDeleteClick={() => handleDeleteOne(item)} />
          </div>
          <div className="order-1 sm:order-2 flex flex-col items-start sm:items-end text-left sm:text-right gap-1 max-w-full sm:max-w-[220px]">
            <div className="text-sm text-muted-foreground whitespace-nowrap">
              {formatDateShort(dateToShow as any)}
            </div>
            {hasValue(byToShow) && (
              <div
                className="text-xs text-muted-foreground/80 truncate"
                title={String(byToShow)}
              >
                {String(byToShow)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleAdd = React.useCallback(() => {
    // Ensure edit param is cleared when adding
    const params = new URLSearchParams(location.search || "");
    if (params.has("profileSetups")) {
      params.delete("profileSetups");
      navigate({ search: params.toString() }, { replace: true });
    }

    setSliderActions({
      action: TOGGLE_PROFILE_SETUP_SLIDER,
      actionMeta: {
        title: "add",
        profile,
        edit: false,
      },
      bulkAction: TOGGLE_PROFILE_SETUP_SLIDER,
    });
    setSliderShow(true);
  }, [profile, location.search, navigate]);

  return (
    <>
      <div className="mt-[4rem] px-1 pb-2 text-sm font-semibold text-foreground">
        Profile Setups
      </div>
      <div className="mt-2 rounded-md border border-border bg-background shadow-[rgba(0,0,0,0.1)_0px_1px_4px]">
        <div className="p-3 flex items-center gap-2 w-full flex-nowrap">
          <div className="flex-1 min-w-0">
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm((e.target as HTMLInputElement).value)
              }
              className="h-9 w-full"
            />
          </div>
          <Button
            type="button"
            onClick={handleAdd}
            className="shrink-0 whitespace-nowrap"
          >
            Add New
          </Button>
        </div>
        <div className="p-3 pt-0">
          <ListView<RFProfileSetupItem>
            key={`rfps-${profile?.SRFProfileId}-${refreshTick}-${searchTerm}`}
            idKey="SRFProfilesSetupId"
            fetchList={fetchList}
            itemRenderer={itemRenderer}
            onItemClick={(it) => openEdit(it as RFProfileSetupItem)}
            className="mt-0"
            externalFilters={{ searchTerm }}
            selectionMode="none"
            selectOnRowClick={false}
            showCheckboxes={false}
            hideSearch={true}
            initialSort={{ field: "SRFProfilesSetupId", dir: "desc" }}
            pageSizeOptions={[10, 20, 50]}
            defaultPageSize={10}
          />
        </div>
      </div>

      {/* Add/Edit Slider (shadcn Sheet) */}
      <RFProfileSetupSheet
        key={`${sliderActions?.actionMeta?.checkedDataItem?.SRFProfilesSetupId || "new"}`}
        handleSlide={handleSlide}
        actions={sliderActions}
        setSelectedState={() => {}}
        dataTableName={RFPROFILESETUP as any}
        show={sliderShow}
        updateData={() => setRefreshTick((t) => t + 1)}
      />

      {/* Delete profile setup confirmation */}
      {setupConfirmOpen && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/40"
            onClick={() => setSetupConfirmOpen(false)}
          />
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-setup-title"
            aria-describedby="confirm-setup-desc"
          >
            <div
              className="w-full max-w-sm rounded-md border border-border bg-popover text-popover-foreground shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-3 border-b border-border">
                <h2
                  id="confirm-setup-title"
                  className="text-base font-semibold"
                >
                  Delete profile setup?
                </h2>
              </div>
              <div className="px-4 py-3">
                <p
                  id="confirm-setup-desc"
                  className="text-sm text-muted-foreground"
                >
                  This action cannot be undone. The record will be permanently
                  removed.
                </p>
              </div>
              <div className="px-4 py-3 flex justify-end gap-2 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSetupConfirmOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    setupDeleteConfirmRef.current?.();
                    setSetupConfirmOpen(false);
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* QR Code Sheet */}
      <QRCodeSheet
        show={showQRModal}
        onClose={() => setShowQRModal(false)}
        singleProfile={singleProfile as any}
        setShowQRModal={setShowQRModal as any}
        title={`Generate QR (${(singleProfile as any)?.SRFProfilesSetupName || ""})`}
      />
    </>
  );
};

export default RFProfileSetupTSX;
